'use client';

/**
 * Classifies a raw search query into one of three modes so the search modal
 * can branch its UI + backend query:
 *
 *   - `ca`     — the query is a contract address (Solana base58 pubkey or a
 *                64-char hex string). Drives the "Exact match" group + snackbar.
 *   - `ticker` — the query has a leading `$` (e.g. `$DEM`). Drives the Ticker
 *                chip + highlighted prefix; the stripped query is searched.
 *   - `text`   — everything else (the default full-text search path).
 *
 * `cleanQuery` is the query with mode-specific prefixes stripped (the `$` for
 * ticker mode) and trimmed — the value that should actually be sent to the
 * search endpoint.
 */

export type SearchQueryMode = 'text' | 'ca' | 'ticker';

export interface SearchQueryModeResult {
  mode: SearchQueryMode;
  cleanQuery: string;
}

// Solana base58 pubkey — 32..44 chars from the base58 alphabet (no 0, O, I, l).
const BASE58_PUBKEY = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
// 64-char hex (32-byte address rendered as hex).
const HEX_64 = /^[0-9a-fA-F]{64}$/;

function isContractAddress(value: string): boolean {
  return BASE58_PUBKEY.test(value) || HEX_64.test(value);
}

export function useSearchQueryMode(query: string): SearchQueryModeResult {
  const trimmed = query.trim();

  if (trimmed.startsWith('$')) {
    return { mode: 'ticker', cleanQuery: trimmed.slice(1).trim() };
  }

  if (isContractAddress(trimmed)) {
    return { mode: 'ca', cleanQuery: trimmed };
  }

  return { mode: 'text', cleanQuery: trimmed };
}
